import { Box, Grid2, Stack, Typography } from '@mui/material';

import { ErrorMessages } from 'src/common/errorMessagesConstants';
import FooterPage from './footerPage';
import HelloSection from 'src/components/helloSection/helloSection';
import LETSCONNECT from '../resources/images/letsConnect.png';
import NavBar from 'src/components/heroSection/navBar';
import { SendIcon } from 'src/resources/icons';
import { StyledNormalButton } from 'src/styledComponents/styledButtons';
import { StyledTextField } from 'src/styledComponents/styledTextField';
import { content } from 'src/common/lang/en-US';
import { handleGetInTouch } from 'src/utils/rest/contactUsRest';
import { useDispatch } from 'react-redux';
import { useState } from 'react';

interface FormData {
    companyName?: string;
    email: string;
    fullName: string;
    message: string;
    mobileNumber: string;
}

const ContactUs: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        companyName: '',
        email: '',
        fullName: '',
        message: '',
        mobileNumber: '',
    });

    const [errors, setErrors] = useState({
        email: '',
        fullName: '',
        message: '',
        mobileNumber: '',
    });

    const dispatch = useDispatch();

    const validateField = (name: string, value: string): string => {
        let error = '';

        if (name === 'email') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!value.trim()) {
                error = ErrorMessages.EMAIL_REQUIRED;
            } else if (!emailRegex.test(value)) {
                error = ErrorMessages.ENTER_VALID_EMAIL;
            }
        }

        if (name === 'mobileNumber') {
            const mobileNumberRegex = /^\d{10}$/;
            if (!value.trim()) {
                error = ErrorMessages.MOBILE_NUMBER_REQUIRED;
            } else if (!mobileNumberRegex.test(value)) {
                error = ErrorMessages.ENTER_VALID_MOBILE_NUMBER;
            }
        }

        if (name === 'fullName' && !value.trim()) {
            error = ErrorMessages.FULL_NAME_REQUIRED;
        }

        if (name === 'message' && !value.trim()) {
            error = ErrorMessages.MESSAGE_REQUIRED;
        }

        return error;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        const error = validateField(name, value);
        setErrors({ ...errors, [name]: error });
    };

    const handleSubmit = async (): Promise<void> => {
        const tempErrors: FormData = {
            email: '',
            fullName: '',
            message: '',
            mobileNumber: '',
        };

        // Validate all fields before submitting
        Object.keys(formData).forEach((field) => {
            const key = field as keyof FormData;
            const error = validateField(field, formData[key] ?? '');
            if (error) {
                tempErrors[key] = error;
            }
        });

        setErrors(tempErrors);
        if (!tempErrors.email || !tempErrors.fullName || !tempErrors.message || !tempErrors.mobileNumber) {
            await handleGetInTouch(
                setIsLoading,
                formData.email,
                formData.mobileNumber,
                formData.fullName,
                formData.companyName ?? '',
                dispatch,
            );
        }
    };
    const { heading, message } = content.contactUs;

    return (
        <Stack>
            <NavBar />
            <HelloSection />
            <Box bgcolor="white">
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={{ xs: '2rem', xxl: '5rem' }}
                    px={{ md: '3rem', xs: '2rem' }}
                    py={{ xs: '2rem', xxl: '10rem' }}
                >
                    <Typography color="textBlack" variant="h2">
                        {heading}
                    </Typography>
                    <Typography color="textBlack" variant="caption1_Thin">
                        {message}
                    </Typography>
                    <Grid2 columns={12} container>
                        <Grid2 py="1rem" size={{ lg: 7, md: 7, sm: 12, xs: 12 }}>
                            <Grid2 columns={12} container spacing={4}>
                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <StyledTextField
                                        error={!!errors.fullName}
                                        helperText={errors.fullName}
                                        label="Full Name"
                                        maxRows={1}
                                        multiline
                                        name="fullName"
                                        onChange={handleChange}
                                        sx={{ color: 'textBlack' }}
                                        value={formData.fullName}
                                    />
                                </Grid2>
                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <StyledTextField
                                        label="Company Name"
                                        maxRows={1}
                                        multiline
                                        name="companyName"
                                        onChange={handleChange}
                                        sx={{ color: 'textBlack' }}
                                        value={formData.companyName}
                                    />
                                </Grid2>

                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <StyledTextField
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        label="Email"
                                        maxRows={1}
                                        multiline
                                        name="email"
                                        onChange={handleChange}
                                        sx={{ color: 'textBlack' }}
                                        value={formData.email}
                                    />
                                </Grid2>
                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <StyledTextField
                                        error={!!errors.mobileNumber}
                                        helperText={errors.mobileNumber}
                                        label="Mobile Number"
                                        maxRows={1}
                                        multiline
                                        name="mobileNumber"
                                        onChange={handleChange}
                                        sx={{ color: 'textBlack' }}
                                        value={formData.mobileNumber}
                                    />
                                </Grid2>

                                <Grid2 size={12}>
                                    <StyledTextField
                                        error={!!errors.message}
                                        fullWidth
                                        helperText={errors.message}
                                        label="Message"
                                        maxRows={4}
                                        multiline
                                        name="message"
                                        onChange={handleChange}
                                        value={formData.message}
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <StyledNormalButton
                                        disabled={
                                            !!errors.email ||
                                            !!errors.fullName ||
                                            !!errors.message ||
                                            !!errors.mobileNumber
                                        }
                                        loading={isLoading}
                                        onClick={handleSubmit}
                                        startIcon={<SendIcon />}
                                        sx={{
                                            '& .MuiCircularProgress-root': {
                                                color: 'secondary.contrastText',
                                            },
                                            backgroundColor:
                                                !!errors.email ||
                                                !!errors.fullName ||
                                                !!errors.message ||
                                                !!errors.mobileNumber ||
                                                isLoading
                                                    ? 'background.bgGray'
                                                    : 'background.default',
                                        }}
                                    >
                                        <Typography color="text.primary" variant="h6_Thin">
                                            Submit
                                        </Typography>
                                    </StyledNormalButton>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                        <Grid2
                            alignItems="flex-start"
                            display="flex"
                            justifyContent="center"
                            size={{ lg: 5, md: 5, sm: 12, xs: 12 }}
                        >
                            <Box component="img" src={LETSCONNECT} />
                        </Grid2>
                    </Grid2>
                </Box>
                <FooterPage />
            </Box>
        </Stack>
    );
};

export default ContactUs;
